/**
 * Dashboard operacional (brief 3.1, Task 10 · redesign Entrega 2): a fila de
 * trabalho do dia. Mesmos seletores do executivo — muda o público do
 * indicador, não a conta.
 *
 * Redesign Entrega 2 (apresentação apenas): layout command-center denso (app de
 * gestão, não editorial). Grid de KPIs com hierarquia forte + um painel com
 * indicador gráfico leve do SLA. `data-page` e os valores dos KPIs são
 * PRESERVADOS; o SLA do gráfico é o MESMO `slaBreakdown` dos cards, sem recalcular.
 *
 * Migrado para JSX real (redesign por perfil, Fase 1 — fecha o cluster final:
 * irmã de `DashboardExecutivo.tsx`, mesmo tratamento).
 */
import React from "react";
import { KpiGrid } from "../components/KpiCard.js";
import { MiniBarChart } from "../components/MiniChart.js";
import { DatasetBoundary } from "../components/DatasetBoundary.js";
import { PeriodOrdersBoundary } from "../components/PeriodOrdersBoundary.js";
import { isOrdersSourceAwaitingContract, type OrdersDataset } from "../orders/dataset.js";
import { applyUnifiedOrderKpis } from "../orders/unified-kpis.js";
import {
  buildDashboardKpis,
  slaBreakdown,
  SLA_FIRST_RESPONSE_TARGET_MINUTES,
  type DashboardDataset
} from "../dashboard/kpis.js";
import type { DashboardViewProps } from "../dashboard/view-props.js";
import { formatCount } from "../format.js";

export interface DashboardOperacionalViewProps extends DashboardViewProps {
  /** Pedidos unificados (Bling + Loja + ...); ausente = KPIs de pedido ficam no recorte do checkout. */
  orders?: OrdersDataset;
  /** Barra de período/atualização já montada. */
  toolbar?: React.ReactElement;
}

export function DashboardOperacionalView({ dataset, now, orders, toolbar }: DashboardOperacionalViewProps): React.ReactElement {
  const at = now ?? new Date();
  const unified = orders && !isOrdersSourceAwaitingContract(orders) ? orders.orders : undefined;
  const kpis = applyUnifiedOrderKpis(buildDashboardKpis(dataset, at), unified);
  const operational = kpis.filter((kpi) => kpi.audience === "operacional" || kpi.audience === "ambos");
  const sla = slaBreakdown(dataset, at);
  return (
    <div className="page page--dashboard-operacional flex flex-col gap-6" data-page="dashboard-operacional">
      {toolbar ?? null}
      <KpiGrid
        kpis={operational}
        title="Visão operacional"
        description={`Fila de trabalho. SLA de primeira resposta humana: ${SLA_FIRST_RESPONSE_TARGET_MINUTES} minutos.`}
      />
      {/* Só desenha o painel do SLA quando há fila a medir — sem fila, o KPI já
          declara a ausência e um gráfico vazio seria ruído (nunca inventar dado). */}
      {sla.queue.length ? (
        <section
          className="flex flex-col gap-3 rounded-panel border border-border bg-surface p-4 shadow-s1"
          aria-label="Fila de SLA de atendimento"
        >
          <h2 className="text-lg font-extrabold text-ink">Fila de SLA de atendimento</h2>
          <MiniBarChart
            title={`Fila de SLA — janela de ${SLA_FIRST_RESPONSE_TARGET_MINUTES} minutos`}
            data-chart="sla-fila"
            bars={[
              {
                label: "Dentro da janela",
                value: sla.within.length,
                display: formatCount(sla.within.length)
              },
              {
                label: "Fora da janela",
                value: sla.breached.length,
                display: formatCount(sla.breached.length),
                emphasis: sla.breached.length > 0
              }
            ]}
          />
        </section>
      ) : null}
    </div>
  );
}

export function DashboardOperacional(): React.ReactElement {
  return (
    <DatasetBoundary>
      {(dataset: DashboardDataset) => (
        <PeriodOrdersBoundary defaultPeriod="mes">
          {(orders: OrdersDataset, context) => <DashboardOperacionalView dataset={dataset} orders={orders} toolbar={context.toolbar} />}
        </PeriodOrdersBoundary>
      )}
    </DatasetBoundary>
  );
}
